import { BaseModel } from './baseModel';
import { ProfileSettings } from './profileSettings';
import { Address } from './address';

export enum ProfileStatus {
  Active = 'active',
  Pending = 'pending',
  Deleted = 'deleted',
  Suspended = 'suspended',
  Inactive = 'inactive',
  Removed = 'removed',
}

/**
 * Statuses that wall the user out of the app entirely. The BE can move a
 * profile into any of these administratively; the FE's only job is to stop
 * at the door and point the user at support (see AccountStatusPage).
 *
 * `pending` is deliberately NOT here — that's the normal pre-registration
 * state every new profile starts in, and RegistrationGate already handles it.
 */
const BLOCKED_STATUSES: ReadonlySet<ProfileStatus> = new Set([
  ProfileStatus.Inactive,
  ProfileStatus.Suspended,
  ProfileStatus.Removed,
  ProfileStatus.Deleted,
]);

export function isBlockedProfileStatus(
  status: ProfileStatus | string | null | undefined,
): boolean {
  return !!status && BLOCKED_STATUSES.has(status as ProfileStatus);
}

export interface LogLevels {
  fatal: boolean;
  error: boolean;
  warn: boolean;
  info: boolean;
  debug: boolean;
  trace: boolean;
}

export interface OnboardingData {
  selectedRole: 'chef' | 'eater';
  quizAnswers: Record<number, string[]>;
  quizCompletedAt: string;
  onboardingCompletedAt: string;
}

export interface ProfileMetadata {
  settings: ProfileSettings;
  logLevels?: LogLevels;
  addresses?: Address[];
  doNotShow?: {
    walkthrough?: boolean;
  };
  onboarding?: OnboardingData;
}

export interface Profile extends Omit<BaseModel, 'metadata'> {
  metadata: ProfileMetadata;
  status: ProfileStatus;
  userId: string;
  extUserId: string | null;
  default: boolean;
  persona: string;
  roles: string[];
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  initials: string | null;
  avatarUrl?: string;
  phoneE164?: string;
  genderEthnicity?: string;
  dob?: string;
  language: string;
  bio?: string;
  /**
   * Default audience reach for listings this user publishes:
   * 1 = friends & family, 2 = friends of friends, 3 = anyone. Default 3.
   * New listings inherit this at create time.
   */
  shareConnectionLevel?: number;
  /**
   * Inbound feed preference — whose listings this user wants to see in their
   * home feed: 1 = friends & family, 2 = friends of friends, 3 = everyone.
   * Default 3. The inverse of `shareConnectionLevel`; enforced server-side.
   */
  seeConnectionLevel?: number;
  /**
   * The user's own dietary restrictions, drawn from the `dietary` category of
   * the BE tags registry (`useTagsRegistry`) — the same chip universe dishes
   * tag themselves with. Applied to the home feed as `dietaryInclude`: a dish
   * qualifies only when it carries ALL of these.
   */
  dietaryTags?: string[];
  /**
   * Allergens the user avoids, from the `allergens` category of the same
   * registry. Applied to the home feed as `allergenExclude`: a dish qualifies
   * only when it carries NONE of these.
   */
  allergenTags?: string[];
  /**
   * Sentinel separating "answered with none" from "never asked". Both tag
   * arrays empty is a legitimate answer, so emptiness alone can't gate the
   * home-feed prompt card. The BE flips this to true on any dietary/allergen
   * tag write. False / absent (every pre-existing profile) means still ask.
   */
  dietaryPrefsSet?: boolean;
  postalCode?: string;
  country?: string;
  lon?: number;
  lat?: number;
  /**
   * The locale the user is most recently operating in. Stamped by the BE on
   * every `/locale` resolve (lat/lon, postal, or auto-create), so it tracks
   * the user's intent even when no profile address is set. The FE hydrates
   * the location store's `locale` from this on auth restore so a returning
   * eater lands back in the locale they last used — not the location-required
   * gate — when they have no captured address.
   */
  currentLocaleId?: string;
  timezone: string;
  invitedById: string;
  invitationAcceptedAt: string | null;
  invitationRejectedAt: string | null;
  businessId: string;
  demo?: boolean;
  invitationAck: boolean;
  business?: { name: string; demo?: boolean };
}

/**
 * Whether the user has answered the dietary-restrictions prompt at all. Reads
 * the sentinel, NOT the tag arrays — a user who answered "no restrictions" has
 * empty arrays and must not be re-prompted. See `Profile.dietaryPrefsSet`.
 */
export function hasAnsweredDietaryPrompt(profile: Profile | null | undefined): boolean {
  return profile?.dietaryPrefsSet === true;
}

/**
 * Whether the user actually has restrictions worth filtering the feed on.
 * Distinct from `hasAnsweredDietaryPrompt`: an "I have none" answer is
 * answered-but-empty, which arms no filter and shows no restrictions pill.
 */
export function hasDietaryRestrictions(profile: Profile | null | undefined): boolean {
  return (profile?.dietaryTags?.length ?? 0) > 0 || (profile?.allergenTags?.length ?? 0) > 0;
}

export function isTeamOwner(profile: Profile | null | undefined): boolean {
  return !!(profile && profile.roles?.includes('owner'));
}

export function isTeamAdmin(profile: Profile | null | undefined): boolean {
  return !!(profile && profile.roles?.includes('admin'));
}

// Accepts any object exposing `metadata.onboarding.selectedRole` —
// covers both `Profile` and the lighter `ProfileSnapshot` used for
// optimistic painting via `useDisplayProfile`. Profile satisfies the
// minimum structurally; no caller change needed.
export function isChef(
  profile: { metadata?: { onboarding?: { selectedRole?: string } } } | null | undefined,
): boolean {
  return profile?.metadata?.onboarding?.selectedRole === 'chef';
}

export function isProfileRegistrationComplete(profile: Profile | null | undefined): boolean {
  if (!profile) return false;
  return !!(profile.firstName?.trim() && profile.lastName?.trim() && profile.displayName?.trim());
}

/**
 * Whether the orderer has a real saved PROFILE address good enough to order —
 * a `type:'profile'` entry with country + postal (the tax minimum; state/city
 * are captured when available but not required).
 *
 * Deliberately distinct from the browse locale (the TopBar pill) and from the
 * top-level `postalCode`/`country` scalars: those scalars get stamped by
 * locale/ZIP browse captures (home feed, device guard, login sync) and
 * represent "where I'm looking", NOT a tax address. They do NOT satisfy this
 * gate — so a browse-only user is correctly prompted to set a profile address
 * before their first order. This is stricter than the backend's tax fallback
 * (`ValidateProfileAddressCountryZip` accepts the scalars) on purpose.
 */
export function hasOrderableProfileAddress(profile: Profile | null | undefined): boolean {
  if (!profile) return false;
  const addr = profile.metadata?.addresses?.find((a) => a.type === 'profile');
  return !!(addr?.country && addr?.postalCode);
}
